/**
 * V2 Step 8 — Done! (never reachable via ?step= URL param).
 * Design from v2-screens-3.jsx:
 *  - Animated gradient background (brand → accent loop)
 *  - Drifting radial blobs overlay
 *  - Animated reveal of the URL card and action list
 *  - "View booking page" as low-emphasis text link (not a primary button)
 *  - Single primary action: Go to dashboard
 *  - Skipped reminders block for Stripe / Calendar
 */
import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { V2C, fontBody, fontDisplay, fontMono } from '../shared/tokens';
import { CheckIcon, CopyIcon } from '../shared/primitives';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';
import { getPublicUrl, getCanonicalDisplayUrl } from '@/utils/publicUrl';

interface Props {
  formData: BusinessSignupData;
}

export default function V2DoneStep({ formData }: Props) {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handle = formData.bookingUrl || 'yourname';
  // viewUrl: dev-aware — opens localhost in dev, getfiggy.link in prod
  const viewUrl = getPublicUrl(handle);
  // displayUrl: always shows the canonical production URL for display/copy
  const displayUrl = getCanonicalDisplayUrl(handle);
  const fullUrl = displayUrl.replace('https://', '');

  const stripeSkipped = !formData.stripeConnected;
  const calendarSkipped = !formData.googleCalendarConnected;

  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 200);
    return () => clearTimeout(t);
  }, []);

  function handleCopy() {
    navigator.clipboard?.writeText(displayUrl).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = displayUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleDashboard() {
    navigate('/dashboard', { replace: true, state: { fromOnboarding: true } });
  }

  function handleLinkInBio() {
    navigate('/dashboard/linkinbio', { replace: true });
  }

  function handleViewPage() {
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div style={{
      width: '100%', minHeight: '100dvh',
      background: `linear-gradient(135deg, ${V2C.brand} 0%, #7874f9 30%, ${V2C.accent} 60%, #7874f9 85%, ${V2C.brand} 100%)`,
      backgroundSize: '300% 300%',
      animation: 'v2DoneGradient 14s ease-in-out infinite',
      fontFamily: fontBody, color: '#fff',
      display: 'flex', flexDirection: 'column',
      paddingTop: 56, paddingBottom: 14,
      overflowY: 'auto', overflowX: 'hidden',
      position: 'relative', boxSizing: 'border-box',
    }}>
      {/* Drifting blobs */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(circle at 20% 25%, rgba(255,255,255,0.22), transparent 45%),
                     radial-gradient(circle at 80% 75%, rgba(255,154,108,0.32), transparent 50%)`,
        animation: 'v2DoneBlobs 12s ease-in-out infinite alternate',
      }} />

      {/* Header */}
      <div style={{ padding: '0 22px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          width: 52, height: 52, margin: '0 auto 10px', borderRadius: 16,
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: revealed ? 'scale(1) rotate(0)' : 'scale(0.5) rotate(-12deg)',
          transition: 'all 0.7s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          <span style={{ fontSize: 24 }}>🎉</span>
        </div>
        <div style={{
          fontFamily: fontDisplay, fontSize: 26, lineHeight: 1.05,
          letterSpacing: -0.5, marginBottom: 4,
        }}>
          Your booking page is <span style={{ fontStyle: 'italic' }}>open</span>.
        </div>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.78)', marginBottom: 12, lineHeight: 1.45, fontFamily: fontBody }}>
          Share the link, take your first booking, and get back to what you do.
        </div>
      </div>

      {/* URL card */}
      <div style={{
        margin: '0 18px 10px', padding: 12, borderRadius: 14,
        background: 'rgba(255,255,255,0.18)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.3)',
        position: 'relative', zIndex: 1,
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 0.15s',
      }}>
        <div style={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)', marginBottom: 3, fontFamily: fontBody,
        }}>
          Your booking link
        </div>
        <div style={{
          fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: '#fff',
          marginBottom: 8, wordBreak: 'break-all',
        }}>
          {fullUrl}
        </div>
        <button
          onClick={handleCopy}
          style={{
            width: '100%', height: 34, borderRadius: 9, border: 'none',
            background: '#fff', color: V2C.brand,
            fontFamily: fontBody, fontSize: 12.5, fontWeight: 700,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {copied
            ? <><CheckIcon size={14} color={V2C.brand} /> Copied!</>
            : <><CopyIcon size={14} color={V2C.brand} /> Copy booking link</>}
        </button>
      </div>

      {/* Action list */}
      <div style={{
        margin: '0 18px 10px', padding: 4, borderRadius: 12,
        background: 'rgba(255,255,255,0.95)',
        position: 'relative', zIndex: 1,
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 0.25s',
      }}>
        <DoneAction
          icon={
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="2.5" width="6" height="6" rx="1.2" stroke={V2C.brand} strokeWidth="1.5"/>
              <rect x="10" y="2.5" width="6" height="6" rx="1.2" stroke={V2C.brand} strokeWidth="1.5"/>
              <rect x="2" y="10.5" width="6" height="6" rx="1.2" stroke={V2C.brand} strokeWidth="1.5"/>
              <rect x="10" y="10.5" width="6" height="6" rx="1.2" stroke={V2C.brand} strokeWidth="1.5"/>
            </svg>
          }
          title="Go to your dashboard"
          detail="Stay on top of everything"
          onClick={handleDashboard}
          divider
        />
        <DoneAction
          icon={
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h12M9 3v12" stroke={V2C.brand} strokeWidth="1.5" strokeLinecap="round"/>
              <rect x="2" y="2" width="14" height="14" rx="3" stroke={V2C.brand} strokeWidth="1.5"/>
            </svg>
          }
          title="Customize your link-in-bio"
          detail="Add links, your photo, and more"
          onClick={handleLinkInBio}
          divider
        />
        <DoneAction
          icon={
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9a6 6 0 1012 0A6 6 0 003 9z" stroke={V2C.brand} strokeWidth="1.5"/>
              <path d="M9 6v3l2 1.5" stroke={V2C.brand} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          }
          title="View your booking page"
          detail="See exactly what clients see"
          onClick={handleViewPage}
        />
      </div>

      {/* Skipped reminders */}
      {(stripeSkipped || calendarSkipped) && (
        <div style={{
          margin: '0 18px', padding: '10px 12px', borderRadius: 10,
          background: 'rgba(0,0,0,0.18)',
          border: '1px solid rgba(255,255,255,0.18)',
          fontSize: 11.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45,
          position: 'relative', zIndex: 1, fontFamily: fontBody,
        }}>
          <strong>Heads up — </strong>
          {stripeSkipped && 'You can still take in-person payments. '}
          {calendarSkipped && "Calendar isn't synced yet. "}
          You can finish setup anytime from your dashboard.
        </div>
      )}

      <style>{`
        @keyframes v2DoneGradient {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes v2DoneBlobs {
          0%   { transform: translate(0, 0) scale(1); opacity: 0.85; }
          50%  { transform: translate(-6%, 4%) scale(1.08); opacity: 1; }
          100% { transform: translate(4%, -3%) scale(1.04); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

function DoneAction({ icon, title, detail, onClick, divider }: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  onClick: () => void;
  divider?: boolean;
}) {
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 11,
      padding: '10px 12px',
      background: 'transparent', border: 'none',
      borderBottom: divider ? `1px solid ${V2C.line}` : 'none',
      cursor: 'pointer', textAlign: 'left', borderRadius: 10,
      boxSizing: 'border-box',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9, background: V2C.brandSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: V2C.ink, lineHeight: 1.2, fontFamily: fontBody }}>{title}</div>
        <div style={{ fontSize: 11.5, color: V2C.mute, marginTop: 2, lineHeight: 1.35, fontFamily: fontBody }}>{detail}</div>
      </div>
      {/* Arrow */}
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2.5 7h9m-3.5-3l3.5 3-3.5 3" stroke={V2C.brand} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}
